import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    Avatar,
    Box,
    Chip,
    CircularProgress,
    Paper,
    Typography,
    useTheme,
    alpha,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PersonIcon from '@mui/icons-material/Person';
import BuildIcon from '@mui/icons-material/Build';
import AdvisorChart from './AdvisorChart';
import { AdvisorChatRole } from '../constants/enums';

const ChatMessage = ({
    role,
    content,
    toolsUsed = [],
    activeTool = null,
    isStreaming = false,
}) => {
    const theme = useTheme();
    const isUser = role === AdvisorChatRole.USER;

    const friendlyToolName = (toolName) => {
        const names = {
            GET_USER_PREFERENCES: 'Retrieving preferences',
            GET_CATEGORIES: 'Examining categories',
            GET_LEDGER_OVERVIEW: 'Calculating net worth & balances',
            SEARCH_TRANSACTIONS: 'Searching transactions',
            GET_SPENDING_SUMMARY: 'Aggregating spending',
            GET_BUDGET: 'Analyzing annual budget',
            GET_RECURRING_TRANSACTIONS: 'Checking recurring bills',
            GET_CREDIT_CARDS: 'Inspecting credit cards',
            GET_EXCHANGE_RATE: 'Converting currency',
            GET_INVESTMENT_POSITIONS: 'Evaluating portfolio holdings',
            GET_INVESTMENT_ACTIVITY: 'Reviewing investment activities',
        };
        return names[toolName] || toolName;
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: isUser ? 'flex-end' : 'flex-start',
                mb: 2.5,
                width: '100%',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isUser ? 'row-reverse' : 'row',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    maxWidth: { xs: '92%', sm: '82%', md: '78%' },
                }}
            >
                {/* Avatar */}
                <Avatar
                    sx={{
                        width: 34,
                        height: 34,
                        bgcolor: isUser ? 'primary.main' : alpha(theme.palette.primary.main, 0.08),
                        color: isUser ? '#ffffff' : 'primary.main',
                        mt: 0.5,
                    }}
                >
                    {isUser ? <PersonIcon fontSize="small" /> : <AutoAwesomeIcon fontSize="small" />}
                </Avatar>

                {/* Message Bubble Container */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start', width: '100%', minWidth: 0 }}>
                    {/* Active tool status banner */}
                    {activeTool && !isUser && (
                        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                                size="small"
                                icon={<CircularProgress size={12} color="inherit" />}
                                label={friendlyToolName(activeTool)}
                                sx={{
                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                                    color: 'primary.main',
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    borderRadius: 1.5,
                                }}
                            />
                        </Box>
                    )}

                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: isUser ? 'primary.main' : 'background.paper',
                            color: isUser ? '#ffffff' : 'text.primary',
                            border: '1px solid',
                            borderColor: isUser ? 'primary.main' : 'divider',
                            wordBreak: 'break-word',
                            width: '100%',
                            boxShadow: isUser
                                ? '0 4px 12px rgba(0, 109, 67, 0.15)'
                                : '0 2px 8px rgba(28, 28, 24, 0.04)',
                        }}
                    >
                        {isUser ? (
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                                {content}
                            </Typography>
                        ) : (
                            <Box
                                sx={{
                                    '& p': { my: 0.75, lineHeight: 1.65, fontSize: '0.95rem' },
                                    '& ul, & ol': { pl: 2.5, my: 0.75 },
                                    '& li': { my: 0.4, lineHeight: 1.6 },
                                    '& h1, & h2, & h3, & h4': {
                                        mt: 1.5,
                                        mb: 0.75,
                                        fontWeight: 700,
                                        fontFamily: '"Manrope", sans-serif',
                                        letterSpacing: '-0.01em',
                                        color: 'text.primary',
                                    },
                                    '& table': {
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        my: 2,
                                        fontSize: '0.875rem',
                                    },
                                    '& th': {
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        p: 1.25,
                                        textAlign: 'left',
                                        fontWeight: 700,
                                        fontSize: '0.75rem',
                                        letterSpacing: '0.08em',
                                        textTransform: 'uppercase',
                                        fontFamily: '"Manrope", sans-serif',
                                        color: 'text.secondary',
                                        bgcolor: alpha(theme.palette.secondary.main, 0.04),
                                    },
                                    '& td': {
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        p: 1.25,
                                        fontVariantNumeric: 'tabular-nums',
                                    },
                                    '& tr:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                                    },
                                    '& blockquote': {
                                        borderLeft: '3px solid',
                                        borderColor: 'primary.main',
                                        my: 1.5,
                                        pl: 2,
                                        py: 0.5,
                                        color: 'text.secondary',
                                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                                        borderRadius: '0 4px 4px 0',
                                    },
                                    '& code': {
                                        bgcolor: alpha(theme.palette.secondary.main, 0.08),
                                        px: 0.7,
                                        py: 0.2,
                                        borderRadius: 1,
                                        fontSize: '0.85em',
                                        fontFamily: 'monospace',
                                    },
                                    '& pre': {
                                        bgcolor: alpha(theme.palette.secondary.main, 0.05),
                                        p: 1.5,
                                        borderRadius: 2,
                                        overflowX: 'auto',
                                        my: 1.5,
                                    },
                                }}
                            >
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                        code({ node, inline, className, children, ...props }) {
                                            const match = /language-(\w+)/.exec(className || '');
                                            if (!inline && match && match[1] === 'chart') {
                                                try {
                                                    const raw = String(children).replace(/\n$/, '');
                                                    const chartConfig = JSON.parse(raw);
                                                    return <AdvisorChart config={chartConfig} />;
                                                } catch (err) {
                                                    return (
                                                        <Box component="pre">
                                                            <code>{children}</code>
                                                        </Box>
                                                    );
                                                }
                                            }
                                            return !inline ? (
                                                <Box component="pre">
                                                    <code className={className} {...props}>
                                                        {children}
                                                    </code>
                                                </Box>
                                            ) : (
                                                <code className={className} {...props}>
                                                    {children}
                                                </code>
                                            );
                                        },
                                    }}
                                >
                                    {content}
                                </ReactMarkdown>

                                {isStreaming && (
                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'inline-block',
                                            width: 6,
                                            height: 15,
                                            bgcolor: 'primary.main',
                                            ml: 0.5,
                                            verticalAlign: 'middle',
                                            animation: 'blink 1s infinite',
                                            '@keyframes blink': {
                                                '0%, 100%': { opacity: 1 },
                                                '50%': { opacity: 0 },
                                            },
                                        }}
                                    />
                                )}
                            </Box>
                        )}
                    </Paper>

                    {/* Tools Used Badge Footer */}
                    {toolsUsed && toolsUsed.length > 0 && !isUser && (
                        <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                            <BuildIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Tools queried:
                            </Typography>
                            {toolsUsed.map((tool, i) => (
                                <Chip
                                    key={i}
                                    label={friendlyToolName(tool)}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        borderRadius: 1,
                                        borderColor: 'divider',
                                        color: 'text.secondary',
                                        bgcolor: alpha(theme.palette.secondary.main, 0.04),
                                    }}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default ChatMessage;
